import type { Tier } from '@kilat/shared';

/**
 * Default loyalty tiers seeded into `tenants/{tid}/loyaltyProgram/config/tiers/{tierId}`
 * (subcollection under the `config` doc — see seed.ts + loyalty-repos.ts).
 *
 * Three tiers — the universally-recognised Bronze / Silver / Gold ladder.
 * Thresholds are picked so a regular weekly customer reaches Silver in ~3 months
 * and Gold in ~12 months at typical spend (~RM 50/visit ≈ 50 points × 4 weeks ×
 * 3 months ≈ 600 points; we tightened it slightly to feel achievable).
 *
 * Perks are minimal at default — operators flesh them out in `/loyalty`.
 * The shape matches `TierSchema`; rank ascends with threshold.
 *
 * Note: `id` is the doc id; `name` is i18n; perks descriptions are i18n.
 */
type SeedTier = Omit<Tier, 'perks'> & { perks: Tier['perks'] };

export const defaultTiers: readonly SeedTier[] = [
  {
    id: 'bronze',
    rank: 0,
    name: { en: 'Bronze', ms: 'Gangsa', zh: '青铜', ta: 'வெண்கலம்' },
    thresholdLifetimePoints: 0,
    perks: [],
    badge: { icon: 'medal', colour: '#cd7f32' },
  },
  {
    id: 'silver',
    rank: 1,
    name: { en: 'Silver', ms: 'Perak', zh: '白银', ta: 'வெள்ளி' },
    thresholdLifetimePoints: 500,
    perks: [
      {
        kind: 'discount_pct',
        value: 5,
        description: {
          en: '5% off every wash',
          ms: '5% diskaun setiap cucian',
          zh: '每次洗车享 5% 折扣',
          ta: 'ஒவ்வொரு கழுவலுக்கும் 5% தள்ளுபடி',
        },
      },
    ],
    badge: { icon: 'medal', colour: '#a8a8a8' },
  },
  {
    id: 'gold',
    rank: 2,
    name: { en: 'Gold', ms: 'Emas', zh: '黄金', ta: 'தங்கம்' },
    thresholdLifetimePoints: 2000,
    perks: [
      {
        kind: 'discount_pct',
        value: 10,
        description: {
          en: '10% off every wash',
          ms: '10% diskaun setiap cucian',
          zh: '每次洗车享 10% 折扣',
          ta: 'ஒவ்வொரு கழுவலுக்கும் 10% தள்ளுபடி',
        },
      },
      {
        kind: 'queue_priority',
        description: {
          en: 'Priority in the queue',
          ms: 'Keutamaan dalam giliran',
          zh: '排队优先',
          ta: 'வரிசையில் முன்னுரிமை',
        },
      },
    ],
    badge: { icon: 'medal', colour: '#d4af37' },
  },
];
